<template>
  <div class="h5-carts-item">
    <div
      class="h5-carts-item-spu"
      :class="{ 'h5-carts-item-spu--disabled': item.disabled }"
    >
      <CartsSelect
        class="h5-carts-item-spu__select"
        :disabled="item.disabled"
        :selected="item.checked"
        @click="handleCheckClick"
      />
      <p
        class="h5-carts-item-spu__title"
        @click="$router.push({ name: 'offer', params: { id: item.offerId }})"
      >
        {{ item.productName }}
      </p>
      <Icon
        class="h5-carts-item-spu__delete"
        name="delete-trash"
        @click="handleDelete"
      />
    </div>
    <p class="h5-carts-item__variants">
      Selected variants: {{ selectVariants }}
    </p>
    <p
      class="h5-carts-item-error"
      v-if="isBelowMOQ"
    >
      <Icon
        class="h5-carts-item-error__icon"
        name="icon-warning-outline"
      />
      The minimum order quantity of {{ item.batchNum }} items has not been met.
    </p>
    <div class="h5-carts-item__sku">
      <CartsSkuItem
        v-for="sku of item.skuDetails"
        :key="sku.skuId"
        :item="sku"
      />
    </div>
    <div class="h5-carts-item-price">
      <p class="h5-carts-item-price__label">
        Total Items:
      </p>
      <p class="h5-carts-item-price__value">
        <IntlCurrency
          :currency="targetCurrency"
          light
        /> {{ totalItemsPrice }}
      </p>
      <p class="h5-carts-item-price__label h5-carts-item-price__divide">
        {{ hasProcessingInChecked ? 'Est.Shipping:' : 'Shipping Cost:' }}
      </p>
      <p class="h5-carts-item-price__value">
        <IntlCurrency
          :currency="targetCurrency"
          light
        /> {{ shippingCost }}
      </p>
    </div>
    <div class="h5-carts-item-total">
      {{ hasProcessingInChecked ? 'Est.Subtotal:' : 'Subtotal:' }}
      <IntlCurrency
        class="h5-carts-item-total__currency"
        :currency="targetCurrency"
      />
      <p class="h5-carts-item-total__value">
        {{ subtotal }}
      </p>
    </div>
  </div>
</template>

<script setup>
import { Icon } from '@/components/Icon'
import CartsSelect from '@/views/Carts/components/CartsSelect.vue'
import { IntlCurrency } from '@/components/NewIntlCurrency'
import CartsSkuItem from './CartsSkuItem.vue'
import { useCurrencyStore } from '@/pinia/currency'
import { useCartSpu } from '@/views/Carts/hooks/useCartSpu'
import { buriedPointUtil } from '@/utils/utils'

const currency = useCurrencyStore()
const targetCurrency = computed(() => currency.current.targetCurrency)

const props = defineProps({
  item: {
    type: Object,
    required: true
  }
})

const { isBelowMOQ, hasProcessingInChecked, selectVariants, totalItemsPrice, shippingCost, subtotal, handleCheck, handleDelete } = useCartSpu(computed(() => props.item))

const handleCheckClick = () => {
  buriedPointUtil({ eventId: 'HCL202', type: 'SPU' })
  handleCheck()
}
</script>

<style lang="less" scoped>
.h5-carts-item {
  position: relative;
  padding: 24px 0 16px;

  &:first-child {
    padding-top: 8px;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    right: 12px;
    left: 40px;
    height: 1px;
    background-color: #EBF1F5;
  }

  &-spu {
    padding: 0 12px;
    display: flex;
    align-items: center;
    gap: 10px;

    &--disabled &__select, &--disabled &__title {
      filter: grayscale(1) opacity(.5);
    }

    &__select {
      font-size: 20px;
    }

    &__title {
      flex: 1;
      -webkit-box-orient: vertical;
      font-family: mon-600;
      font-size: 13px;
      line-height: 18px;
      overflow: hidden;
      text-overflow: ellipsis;
      display: -webkit-box;
      -webkit-line-clamp: 2;
    }

    &__delete {
      font-size: 20px;
    }
  }

  &-spu--disabled + &__variants {
    filter: grayscale(1) opacity(.5);
  }

  &__variants {
    margin-left: 42px;
    margin-top: 4px;
    font-family: mon-500;
    font-size: 12px;
    line-height: 16px;
    color: #5C5C5C;
  }

  &-error {
    margin-top: 8px;
    margin-left: 42px;
    font-family: mon-500;
    font-size: 12px;
    line-height: 16px;
    color: #7A0505;

    &__icon {
      font-size: 14px;
    }

    &__contact {
      margin-left: 6px;
      text-decoration: underline;
      color: #232323;
    }
  }

  &__sku {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 8px 0 16px;
  }

  &-discount {
    margin-bottom: 12px;
    justify-content: flex-end;
    padding: 0 12px;
  }

  &-price {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    padding: 0 12px;
    gap: 4px;
    font-family: mon-500;
    font-size: 12px;
    line-height: 16px;

    &__divide {
      margin-left: 4px;
    }

    &__label {
      color: #6E717A;
    }

    &__value {
      display: flex;
      align-items: center;
      color: #232323;
    }
  }

  &-total {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    margin: 12px 12px 0 36px;
    padding: 8px;
    border-radius: 4px;
    background-color: #F8F8F8;
    font-family: mon-500;
    font-size: 12px;
    line-height: 16px;
    color: #6E717A;

    &__currency {
      margin-left: 4px;
      font-size: 14px;
      color: #232323;
    }

    &__value {
      margin-left: 2px;
      font-family: mon-700;
      font-size: 14px;
      color: #232323;
    }
  }
}
</style>
